"use client";

import { useParams, useNavigate } from "react-router-dom";
import {
  Flex,
  Heading,
  Text,
  Link,
  Collection,
  Card,
  Badge,
  Grid,
} from "@aws-amplify/ui-react";
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { generateClient } from "aws-amplify/api";

import { Helmet } from "react-helmet";
import { VariationCreateForm } from "../../ui-components";
import { Schema } from "../../../amplify/data/resource";
import { hasAccess } from "../../acl/groups";
import { FaArrowLeft, FaPencil, FaPlus } from "react-icons/fa6";
import { I18n } from "aws-amplify/utils";
import LoadingDialog from "../../components/LoadingDialog";

import PaperComponent from "../../components/PaperComponent";

const client = generateClient<Schema>();

type Pattern = Schema["Pattern"]["type"];
type Variation = Schema["Variation"]["type"];
type DanceStyle = Schema["DanceStyle"]["type"];

interface PatternViewProps {
  groups: string[];
}

const PatternView: React.FC<PatternViewProps> = ({ groups }) => {
  const navigate = useNavigate();

  const { id } = useParams();

  const [patternRecord, setPattern] = React.useState<Pattern>({} as Pattern);
  const [variations, setVariations] = React.useState<Variation[]>([]);
  const [danceStyle, setDanceStyle] = React.useState<DanceStyle>(
    {} as DanceStyle
  );
  const [open, setOpen] = React.useState(false);

  const [loadingMap, setLoadingMap] = React.useState<{
    [key: string]: boolean;
  }>({});

  const markLoading = (key: string, value: boolean) => {
    setLoadingMap((prev) => ({ ...prev, [key]: value }));
  };

  const onClickAddVariation = () => {
    setOpen(true);
  };

  const onCloseDialog = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    markLoading("pattern", true);
    const patternSub = client.models.Pattern.observeQuery({
      filter: {
        id: { eq: id },
      },
    }).subscribe({
      next: ({ items }) => {
        setPattern(items[0]);
        markLoading("pattern", false);
      },
    });
    const variationQueryStatus = hasAccess(groups, "variations", "update")
      ? {}
      : { status: { eq: true } };

    markLoading("variations", true);
    const variationSub = client.models.Variation.observeQuery({
      filter: {
        patternVariationsId: { eq: id },
        ...variationQueryStatus,
      },
    }).subscribe({
      next: ({ items }) => {
        items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setVariations([...items]);
        markLoading("variations", false);
      },
    });
    return () => {
      variationSub.unsubscribe();
      patternSub.unsubscribe();
    };
  }, [id, groups]);

  React.useEffect(() => {
    if (patternRecord && patternRecord.danceStylePatternsId) {
      markLoading("danceStyle", true);
      const danceStyleSub = client.models.DanceStyle.observeQuery({
        filter: {
          id: { eq: patternRecord.danceStylePatternsId },
        },
      }).subscribe({
        next: ({ items }) => {
          setDanceStyle(items[0]);
          markLoading("danceStyle", false);
        },
      });
      return () => {
        danceStyleSub.unsubscribe();
      };
    }
  }, [patternRecord]);

  return (
    <React.Fragment>
      {Object.values(loadingMap).some((loading) => loading) ? (
        <LoadingDialog loadingMap={loadingMap} />
      ) : (
        <Flex direction="column" gap="1rem">
          <Helmet>
            <title>{patternRecord?.name}</title>
          </Helmet>
          <Flex marginTop={"1rem"}>
            <Heading level={1}>{patternRecord?.name}</Heading>
          </Flex>
          <Flex>
            <Link onClick={() => navigate(-1)}>
              <FaArrowLeft aria-label={I18n.get("Back")} />
            </Link>
            {hasAccess(groups, "patterns", "update") && (
              <Link href={`/patterns/${patternRecord?.id}/update`}>
                <FaPencil aria-label={I18n.get("Edit")} />
              </Link>
            )}
          </Flex>
          <Flex direction="column">
            <Flex>
              <Heading>{I18n.get("Dance Style")}</Heading>
              <Text>
                <Link
                  href={`/dance-styles/${patternRecord?.danceStylePatternsId}/view`}
                >
                  {danceStyle
                    ? danceStyle.name
                    : patternRecord.danceStylePatternsId}
                </Link>
              </Text>
            </Flex>
            <Flex>
              <Heading>{I18n.get("Status")}</Heading>
              <Text>
                {patternRecord?.status
                  ? I18n.get("Active")
                  : I18n.get("Disabled")}
              </Text>
            </Flex>
            <Flex>
              <Heading>{I18n.get("Order")}</Heading>
              <Text>{patternRecord?.order && patternRecord.order / 10}</Text>
            </Flex>
          </Flex>
          <Flex>
            <Heading level={2}>{I18n.get("Variations")}</Heading>
          </Flex>
          {hasAccess(groups, "variations", "create") && (
            <Flex>
              <Link onClick={onClickAddVariation}>
                <FaPlus aria-label={I18n.get("Add Variation")} />
              </Link>
            </Flex>
          )}

          <Grid>
            <Collection
              items={variations}
              type="list"
              direction="row"
              wrap="wrap"
              alignContent="flex-start"
              searchPlaceholder={I18n.get("Type to search...")}
              isPaginated
              isSearchable
            >
              {(variation: Variation) => (
                <Card
                  key={variation.id}
                  title={"#" + (variation.order && variation.order / 10)}
                  variation="outlined"
                  borderRadius="medium"
                  alignSelf="flex-start"
                  width={{ base: "100%", medium: "48.8%", large: "18.9%" }}
                >
                  <Flex
                    direction="column"
                    marginBottom="1rem"
                    minHeight={{ base: "2lh", large: "3lh" }}
                    textAlign="center"
                  >
                    <Heading>
                      <Link href={`/variations/${variation.id}/view`}>
                        {"#" + (variation.order && variation.order / 10)}
                      </Link>
                      {hasAccess(groups, "variations", "update") && (
                        <>
                          &nbsp;{" "}
                          <Link href={`/variations/${variation.id}/update`}>
                            <FaPencil aria-label={I18n.get("Edit")} />
                          </Link>
                        </>
                      )}
                    </Heading>
                    <Text>{variation.rhytm}</Text>
                    <Text>
                      {variation.rhytmMan && (
                        <Text>
                          {I18n.get("Man")}: {variation.rhytmMan}{" "}
                        </Text>
                      )}
                    </Text>
                  </Flex>
                  <Flex
                    direction="row"
                    wrap="wrap"
                    justifyContent="center"
                    marginBottom="1rem"
                  >
                    <Badge backgroundColor={"orange.40"}>
                      {variation.hold}
                    </Badge>
                    <Badge
                      backgroundColor={
                        variation.status ? "green.40" : "gray.40"
                      }
                    >
                      {variation.status
                        ? I18n.get("Active")
                        : I18n.get("Disabled")}
                    </Badge>
                  </Flex>
                </Card>
              )}
            </Collection>
          </Grid>
          <Dialog
            open={open}
            onClose={onCloseDialog}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
          >
            <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
              Add Variation
            </DialogTitle>
            <DialogContent>
              <VariationCreateForm
                pattern={patternRecord}
                danceStyle={danceStyle}
              />
            </DialogContent>
          </Dialog>
        </Flex>
      )}
    </React.Fragment>
  );
};

export default PatternView;
